import { useCallback, useEffect, useRef, useState } from "react";

const IDLE_BEFORE_WARNING_MS = 14 * 60 * 1000; // 14 minutes idle → show dialog
const WARNING_DURATION_S = 60; // 60-second countdown in the dialog

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "click",
];

export interface InactivityState {
  showWarning: boolean;
  secondsRemaining: number;
  stayLoggedIn: () => void;
}

/**
 * Tracks user inactivity.
 * - After 14 minutes idle: shows a warning dialog with a 60-second countdown.
 * - Once the warning is visible, activity events NO LONGER reset the timer —
 *   only an explicit "Stay logged in" click does.
 * - If the countdown reaches 0, `onLogout` is called.
 */
export function useInactivityLogout(
  isAuthenticated: boolean,
  onLogout: () => void,
): InactivityState {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(WARNING_DURATION_S);

  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const warningActiveRef = useRef(false);
  const onLogoutRef = useRef(onLogout);

  useEffect(() => {
    onLogoutRef.current = onLogout;
  }, [onLogout]);

  const clearAllTimers = useCallback(() => {
    if (idleTimerRef.current !== null) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (logoutTimerRef.current !== null) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (countdownRef.current !== null) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const startIdleTimer = useCallback(() => {
    clearAllTimers();
    warningActiveRef.current = false;

    idleTimerRef.current = setTimeout(() => {
      // User has been idle for 14 minutes — show the warning dialog
      warningActiveRef.current = true;
      setSecondsRemaining(WARNING_DURATION_S);
      setShowWarning(true);

      // Tick countdown every second
      countdownRef.current = setInterval(() => {
        setSecondsRemaining((prev) => {
          if (prev <= 1) {
            if (countdownRef.current !== null) {
              clearInterval(countdownRef.current);
              countdownRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Force-logout after the warning duration
      logoutTimerRef.current = setTimeout(() => {
        warningActiveRef.current = false;
        setShowWarning(false);
        onLogoutRef.current();
      }, WARNING_DURATION_S * 1000);
    }, IDLE_BEFORE_WARNING_MS);
  }, [clearAllTimers]);

  // "Stay logged in" — user explicitly dismissed the warning
  const stayLoggedIn = useCallback(() => {
    setShowWarning(false);
    startIdleTimer();
  }, [startIdleTimer]);

  useEffect(() => {
    if (!isAuthenticated) {
      clearAllTimers();
      setShowWarning(false);
      return;
    }

    const handleActivity = () => {
      // Once the warning dialog is visible, ignore passive activity —
      // the user must click "Stay logged in" explicitly.
      if (!warningActiveRef.current) {
        startIdleTimer();
      }
    };

    startIdleTimer();

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      clearAllTimers();
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [isAuthenticated, startIdleTimer, clearAllTimers]);

  return { showWarning, secondsRemaining, stayLoggedIn };
}
